<template>
  <section>
    <div class="interview-request-container">
      <SitterDetails />
      <HorizontalDivider class="mt-24" />

      <FormStepper
        v-if="sitter"
        ref="formStepper"
        :steps="steps"
        :loading="submitting"
        :pageHasBottomNav="true"
        :lastStepButtonText="'Send Request'"
        @submit="handleSubmit"
      >
        <template #progressBar="{ currentStep }">
          <div role="region" aria-label="Interview request progress">
            <span class="text-caption text-grey-text" aria-live="polite" aria-label="Current step progress">
              Step {{ currentStep }} of {{ steps.length }}
            </span>
          </div>
        </template>
      </FormStepper>
    </div>
  </section>
</template>

<script lang="ts">
import { mapActions } from 'vuex';
import { redirect } from '@/helpers/redirects';
import { SITTER_FOR_INTERVIEW_QUERY } from '@/graphql/interviews/queries';
import { CREATE_INTERVIEW_REQUEST } from '@/graphql/interviews/mutations';
import SitterDetails from './SitterDetails.vue';
import FormStepper from '@/components/form_stepper/FormStepper.vue';
import HorizontalDivider from '@/components/divider/HorizontalDivider.vue';
import BasicDetailsStep from './steps/BasicDetailsStep.vue';
import InterviewDetails from './steps/InterviewDetails.vue';
import ConfirmationStep from './steps/ConfirmationStep.vue';

export default {
  name: 'CreateRequest',
  components: {
    SitterDetails,
    FormStepper,
    HorizontalDivider,
  },
  data(): any {
    return {
      loading: true,
      error: null as string | null,
      sitter: null as any,
      submitting: false,
    };
  },
  computed: {
    steps() {
      return [
        {
          name: 'Basic Details',
          title: 'Interview basics',
          required: true,
          component: BasicDetailsStep,
          alwaysFullWidthButton: false,
          xlCard: true,
          nextStepCallback: () => this.saveBasicDetailsToStore(),
          previousStepCallback: () => {},
        },
        {
          name: 'Interview Details',
          title: 'Interview details',
          required: true,
          component: InterviewDetails,
          alwaysFullWidthButton: false,
          xlCard: true,
          nextStepCallback: () => this.saveInterviewDetailsToStore(),
          previousStepCallback: () => {},
        },
        {
          name: 'Confirmation',
          title: 'Review your request',
          required: true,
          component: ConfirmationStep,
          alwaysFullWidthButton: false,
          xlCard: true,
          nextStepCallback: () => {},
          previousStepCallback: () => {},
        },
      ];
    },
  },
  async mounted() {
    await this.fetchData();
  },
  methods: {
    ...mapActions('interviewRequest', ['setSitter', 'saveBasicDetails', 'saveInterviewDetails', 'clearInterviewRequest']),
    async fetchData() {
      try {
        this.loading = true;
        this.error = null;

        const sitterUuid = this.$route.params.sitter_uuid || this.$store.getters['interviewRequest/sitterUuid'];

        if (!sitterUuid) {
          this.error = 'Sitter profile could not be found';
          return;
        }

        const sitterRes = await this.$apollo.query({
          query: SITTER_FOR_INTERVIEW_QUERY,
          variables: { accountId: sitterUuid },
          fetchPolicy: 'network-only',
        });

        const sitterData = sitterRes?.data?.account;

        if (!sitterData) {
          this.error = 'Sitter profile could not be found';
          return;
        }

        this.sitter = sitterData;
        this.setSitter(sitterData);
      } catch (error) {
        console.error('Error fetching interview request data:', error);
        this.error = 'Sitter profile could not be found';
      } finally {
        this.loading = false;
      }
    },
    redirectToBookings() {
      redirect('/bookings');
    },
    saveBasicDetailsToStore() {
      const stepComponent = this.$refs.formStepper?.$refs?.stepComponent;
      if (stepComponent && stepComponent.saveToStore) {
        stepComponent.saveToStore();
      }
    },
    saveInterviewDetailsToStore() {
      const stepComponent = this.$refs.formStepper?.$refs?.stepComponent;
      if (stepComponent && stepComponent.saveToStore) {
        stepComponent.saveToStore();
      }
    },
    async handleSubmit() {
      await this.submitRequest();
    },
    async submitRequest() {
      this.submitting = true;

      try {
        const submissionVariables = this.$store.getters['interviewRequest/submissionVariables'];

        if (!submissionVariables) {
          throw new Error('Unable to get interview request data');
        }

        await this.$apollo.mutate({
          mutation: CREATE_INTERVIEW_REQUEST,
          variables: submissionVariables,
        });

        this.clearInterviewRequest();

        this.$router.push({
          name: 'interview-request-whats-next',
          params: { sitter_uuid: this.sitter.id },
        });
      } catch (error) {
        console.error('Error creating interview request:', error);
        this.error = 'Failed to create interview request. Please try again.';
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.interview-request-container {
  max-width: 688px;
  margin: 0 auto;
  padding: 16px 0;
  width: 100%;

  // Override FormStepper's progress bar to fit within container and remove background
  :deep(.bg-grey-background-subdued) {
    max-width: 688px;
    padding: 16px 0;
    background-color: transparent !important;
    box-shadow: none !important;
  }

  // Override FormStepper's collapsed height from 70px to 40px
  :deep(.collapsed) {
    height: 40px !important;
  }

  // Add left padding to step counter to align with content
  :deep([role='region']) {
    padding-left: 0.75rem;
  }
}
</style>
